import { setSelectedElementKey } from "@/store/iframeSlice";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { toast } from "sonner";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { TempoElement } from "@/utils/tempoElement";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { getStoriesForComponent } from "@/utils/jsx/extractElementsUtils";
import axios from "axios";
import { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { ProjectRunMode, ReactFramework } from "@/utils/templateProjects";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useAssetComponents } from "../useAssetComponents";
import { useSession } from "@clerk/clerk-react";
import useActivityStore from "@/store/zustand/activityStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import {
  StoryboardDelta,
  UpdateStoryboardsChange,
} from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { usePagesStore } from "@/store/zustand/pagesStore";

export const useDeleteStoryboards = () => {
  const canvasId = useCanvasId();

  const { componentKeyToComponent } = useAssetComponents();
  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );
  const { session } = useSession();

  const dispatch = useDispatch<any>();

  const canvas = useActiveCanvas();
  const project = useActiveProject();

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey,
  );
  // @ts-expect-error TS2538
  const canvasComponent = componentKeyToComponent?.[canvasComponentKey];

  const [confirmDeleteStoryboardIds, setConfirmDeleteStoryboardIds] = useState<
    string[]
  >([]);

  const confirmDeleteStoryboards = useMemo(() => {
    return confirmDeleteStoryboardIds.map((storyboardId) => {
      // @ts-expect-error TS18047
      return canvas.storyboards.find((sb: any) => sb.id === storyboardId);
    });
  }, [confirmDeleteStoryboardIds, canvas]);

  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const deleteStoryboardIds = useCallback(
    async (storyboardIds: string[]) => {
      if (!canvasRef.current) {
        return;
      }

      const storyboardsToDelete = canvasRef.current.storyboards.filter(
        (sb: any) => {
          return storyboardIds.includes(sb.id);
        },
      );

      // STORY storyboards need special handling via /deleteUnsavedComponent for JSX removal
      // COMPONENT storyboards have file deletion handled by updatePageStoryboards via deltas
      const storyboardsToDeleteOnBackend = storyboardsToDelete.filter(
        (sb: any) => sb.type === STORYBOARD_TYPES.STORY,
      );

      // Get page info for deltas
      const pagesStore = usePagesStore.getState();
      const selectedPageId = pagesStore.selectedPageId[canvasRef.current.id];
      const pageConfigPath = selectedPageId
        ? pagesStore.getPageSourcePath(canvasRef.current.id, selectedPageId)
        : undefined;

      const storyboardDeltas: StoryboardDelta[] = storyboardsToDelete.map(
        (sb: any) => ({
          changeType: "remove" as const,
          storyboardId: sb.id,
          pageId: selectedPageId || "",
          pageConfigPath,
          storyboardUpdateDict: {},
        }),
      );

      if (!canvasRef.current) {
        toast.error("Canvas reference not available");
        console.error("Canvas reference not available");
        return;
      }

      const storyboardsForStoryTypes = storyboardsToDelete.filter(
        (sb: any) => sb.type === STORYBOARD_TYPES.STORY,
      );

      if (storyboardsForStoryTypes.length > 0 && !canvasComponent) {
        throw "Deleting story type storyboards can only be allowed when there is a canvas component";
      }

      const stories = getStoriesForComponent(
        {
          framework: project?.framework as ReactFramework,
        },
        treeElementLookup,
        canvasComponent,
      );

      const storyTreeElements = stories.filter((story: JsxElement) => {
        return storyboardsForStoryTypes.find(
          (storyboard: any) => story.componentName === storyboard.name,
        );
      });

      const deleteChange = new UpdateStoryboardsChange({
        canvasId: canvasRef.current.id,
        deltas: storyboardDeltas,
        deleteStoryboardFields: {
          storyboardIdsToDeleteOnBackend: storyboardsToDeleteOnBackend.map(
            (sb: any) => sb.id,
          ),
          storyTreeElementsToDeleteOnBackend: storyTreeElements,
          deleteSubcanvasId:
            canvasRef.current.id !== canvasId
              ? canvasRef.current.id
              : undefined,
        },
      });

      useDesignCommandManager.getState().executeCommand(deleteChange);

      // Clear the errors for the storyboards that were deleted
      storyboardsToDelete.forEach((sb: any) => {
        useDevServerStore.getState().clearErrorsForStoryboard(sb.id);
      });

      // Clear the selected state
      dispatch(setSelectedElementKey(null));
      return;
    },
    [
      project,
      canvasComponent,
      treeElementLookup,
      dispatch,
      canvasId,
      canvasRef,
    ],
  );

  const deleteStoryboards = useCallback(
    async (storyboardElements: TempoElement[]) => {
      const storyboardIdsToDelete = new Set(
        storyboardElements.map((el) => el.storyboardId),
      );

      // @ts-expect-error TS18047
      const hasSavedStories = canvasRef.current.storyboards.find(
        (sb: any) =>
          storyboardIdsToDelete.has(sb.id) &&
          sb.type === STORYBOARD_TYPES.STORY &&
          sb.saved === true,
      );

      if (hasSavedStories) {
        setConfirmDeleteStoryboardIds(Array.from(storyboardIdsToDelete));
      } else {
        await deleteStoryboardIds(Array.from(storyboardIdsToDelete));
      }
    },
    [deleteStoryboardIds],
  );

  const renderConfirmDeleteDialog = useCallback(
    () => (
      <AlertDialog open={Boolean(confirmDeleteStoryboardIds.length)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="flex flex-col align-start gap-2">
                <div className="text-secondary-900">
                  This will delete the following stories permanently:
                </div>
                {confirmDeleteStoryboards
                  .filter((storyboard: any) => {
                    return (
                      storyboard.type === STORYBOARD_TYPES.STORY &&
                      storyboard.saved
                    );
                  })
                  .map((storyboard: any) => {
                    return <div>- {storyboard.name}</div>;
                  })}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setConfirmDeleteStoryboardIds([])}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant={"secondary"}
              onClick={async () => {
                setConfirmDeleteStoryboardIds([]);
                await deleteStoryboardIds(confirmDeleteStoryboardIds);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ),
    [confirmDeleteStoryboardIds, confirmDeleteStoryboards, deleteStoryboardIds],
  );

  return {
    deleteStoryboards,
    renderConfirmDeleteDialog,
  };
};
